import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useState } from "react";
import { sendTestWebhook } from "../../api/settings";
import { Button } from "../medical/shared-components/button";
import { getSampleData } from "./webhook-tester-data";

type WebhookEventType = "patient.admit" | "patient.transfer" | "patient.discharge";

export default function WebhookTester() {
  const [selectedAdtType, setSelectedAdtType] = useState<WebhookEventType>("patient.admit");
  const [isSending, setIsSending] = useState(false);
  const [response, setResponse] = useState<string>("");
  const toast = useToast();
  const sampleData = getSampleData();

  const currentSample = sampleData[selectedAdtType];

  async function handleSendWebhook() {
    setIsSending(true);
    try {
      const response = await sendTestWebhook(currentSample);
      setResponse(`${JSON.stringify(response, null, 2)}`);
      toast({
        title: "Sample Payload Sent",
        description: `Successfully sent ${selectedAdtType} sample payload`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error sending sample payload:", error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status || "Unknown";
        const responseData = error.response?.data;
        setResponse(`Status: ${status}\n\n${JSON.stringify(responseData, null, 2)}`);
        toast({
          title: "Sample Payload Failed",
          description: `Failed: ${status}`,
          status: "error",
          duration: 3000,
        });
      } else {
        setResponse(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
        toast({
          title: "Test Error",
          description: "Failed to send sample payload",
          status: "error",
          duration: 3000,
        });
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Box mt={10}>
      <VStack align="stretch" spacing={4}>
        <Heading size="sm">Test events</Heading>
        <Text>Send sample payloads to test your webhook integration.</Text>

        <Flex direction={{ base: "column", md: "row" }} gap={4} align="center">
          <FormControl flex={1}>
            <Select
              value={selectedAdtType}
              onChange={e => setSelectedAdtType(e.target.value as WebhookEventType)}
            >
              <option value="patient.admit">Patient Admit</option>
              <option value="patient.transfer">Patient Transfer</option>
              <option value="patient.discharge">Patient Discharge</option>
            </Select>
          </FormControl>

          <Button
            onClick={handleSendWebhook}
            isLoading={isSending}
            loadingText="Sending..."
            size="sm"
          >
            Send Payload
          </Button>
        </Flex>

        <Box>
          <FormLabel mb={2}>Sample Payload (JSON):</FormLabel>
          <Textarea
            value={JSON.stringify(currentSample, null, 2)}
            readOnly
            fontFamily="mono"
            fontSize="sm"
            minHeight="300px"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
          />
        </Box>

        {response && (
          <Box>
            <FormLabel mb={2}>Response:</FormLabel>
            <Textarea
              value={response}
              readOnly
              fontFamily="mono"
              fontSize="sm"
              minHeight="150px"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
}
